import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Plus, X, Send, Upload, FileText } from 'lucide-react';

export default function CreateBrief() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [methods, setMethods] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [form, setForm] = useState({
    title: '',
    company_name: '',
    description: '',
    objectives: '',
    target_audience: '',
    method: '',
    sector: '',
    budget_min: '',
    budget_max: '',
    timeline: '',
    deadline: '',
    attachment_url: '',
    languages_required: [],
    location: ''
  });
  const [newLanguage, setNewLanguage] = useState('');
  
  useEffect(() => {
    const fetchConstants = async () => {
      try {
        const [methodsRes, sectorsRes] = await Promise.all([
          api.get('/constants/methods'),
          api.get('/constants/sectors')
        ]);
        setMethods(methodsRes.data.methods || []);
        setSectors(sectorsRes.data.sectors || []);
      } catch (error) {
        console.error('Error fetching constants:', error);
      }
    };
    fetchConstants();
  }, []);
  
  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };
  
  const addLanguage = () => {
    if (newLanguage.trim()) {
      setForm(prev => ({
        ...prev,
        languages_required: [...prev.languages_required, newLanguage.trim()]
      }));
      setNewLanguage('');
    }
  };
  
  const removeLanguage = (index) => {
    setForm(prev => ({
      ...prev,
      languages_required: prev.languages_required.filter((_, i) => i !== index)
    }));
  };
  
  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx)$/i)) {
      alert('Please upload a PDF or Word document');
      return;
    }
    
    setUploading(true);
    try {
      const sigResponse = await api.get('/cloudinary/signature?folder=briefs/attachments&resource_type=raw');
      const sig = sigResponse.data;
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);
      
      const uploadUrl = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/raw/upload`;
      const uploadResponse = await fetch(uploadUrl, { method: 'POST', body: formData });
      const result = await uploadResponse.json();
      
      if (result.secure_url) {
        handleChange('attachment_url', result.secure_url);
      } else {
        alert('Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim() || !form.description.trim()) {
      alert('Please fill in the title and description');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        budget_min: form.budget_min ? parseFloat(form.budget_min) : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null
      };
      
      await api.post('/briefs', payload);
      navigate('/client/briefs');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create brief');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <DashboardLayout title="Post New Brief">
      <form onSubmit={handleSubmit} className="max-w-4xl">
        {/* Basic Info */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Basic Information</h3>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Title *</label>
              <input
                type="text"
                placeholder="e.g., Consumer Insights Study - Snacking Category"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Company / Brand Name</label>
              <input
                type="text"
                placeholder="e.g., Leading FMCG Company"
                value={form.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Description *</label>
            <textarea
              placeholder="Describe the research brief, context, and what you're looking for..."
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none resize-none"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Objectives</label>
            <textarea
              placeholder="What are the key research objectives?"
              value={form.objectives}
              onChange={(e) => handleChange('objectives', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none resize-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Target Audience</label>
            <input
              type="text"
              placeholder="e.g., Urban millennials, SEC A/B, ages 25-40"
              value={form.target_audience}
              onChange={(e) => handleChange('target_audience', e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
            />
          </div>
        </div>
        
        {/* Category */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Category</h3>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Method</label>
              <select
                value={form.method}
                onChange={(e) => handleChange('method', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none bg-white"
              >
                <option value="">Select a method</option>
                {methods.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Sector</label>
              <select
                value={form.sector}
                onChange={(e) => handleChange('sector', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none bg-white"
              >
                <option value="">Select a sector</option>
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
        
        {/* Languages */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Languages Required</h3>
          
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              placeholder="e.g., Hindi, Tamil"
              value={newLanguage}
              onChange={(e) => setNewLanguage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
              className="flex-1 px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
            />
            <button type="button" onClick={addLanguage} className="btn-client px-3 py-2">
              <Plus size={18} />
            </button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {form.languages_required.map((lang, i) => (
              <span key={i} className="px-3 py-1 bg-neutral-100 text-neutral-700 text-sm flex items-center gap-2">
                {lang}
                <button type="button" onClick={() => removeLanguage(i)} className="hover:text-red-500">
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        </div>
        
        {/* Budget & Timeline */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Budget & Timeline</h3>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Budget Min (₹)</label>
              <input
                type="number"
                placeholder="e.g., 200000"
                value={form.budget_min}
                onChange={(e) => handleChange('budget_min', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Budget Max (₹)</label>
              <input
                type="number"
                placeholder="e.g., 500000"
                value={form.budget_max}
                onChange={(e) => handleChange('budget_max', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Timeline</label>
              <input
                type="text"
                placeholder="e.g., Q2 2026"
                value={form.timeline}
                onChange={(e) => handleChange('timeline', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Response Deadline</label>
              <input
                type="text"
                placeholder="e.g., April 15, 2026"
                value={form.deadline}
                onChange={(e) => handleChange('deadline', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
              />
            </div>
          </div>
        </div>
        
        {/* Location */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Location</h3>
          
          <input
            type="text"
            placeholder="e.g., Delhi, Mumbai, Bangalore"
            value={form.location}
            onChange={(e) => handleChange('location', e.target.value)}
            className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
          />
        </div>
        
        {/* Attachment */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
            <FileText size={18} className="inline mr-2" />
            Brief Document (Optional)
          </h3>
          <p className="text-sm text-neutral-500 mb-3">Upload a detailed brief document (PDF/Word) that researchers can download.</p>
          
          {form.attachment_url ? (
            <div className="flex items-center gap-3 bg-neutral-50 px-4 py-3 border border-neutral-200">
              <FileText size={20} className="text-[#0D7377]" />
              <a href={form.attachment_url} target="_blank" rel="noopener noreferrer" className="text-[#0D7377] hover:underline text-sm flex-1 truncate">
                Brief Document Uploaded
              </a>
              <button type="button" onClick={() => handleChange('attachment_url', '')} className="text-neutral-400 hover:text-red-500">
                <X size={16} />
              </button>
            </div>
          ) : (
            <label className="flex items-center gap-3 px-4 py-4 border-2 border-dashed border-neutral-200 cursor-pointer hover:border-[#0D7377] transition-colors" data-testid="brief-attachment-upload">
              <Upload size={22} className="text-neutral-400" />
              <span className="text-neutral-600 text-sm">{uploading ? 'Uploading...' : 'Choose PDF or Word file'}</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleAttachmentUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          )}
        </div>
        
        {/* Submit */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={saving}
            className="btn-client px-8 py-3 flex items-center gap-2"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Send size={18} />
                Post Brief
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/client/briefs')}
            className="px-8 py-3 border border-neutral-200 hover:bg-neutral-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </DashboardLayout>
  );
}
